import { isValidEventTypeKey } from '../eventTypes';
import { formatTimeForApi } from './buildEventFormData';
import { dishNameFieldKey, dishNameRequiredMessage } from './menuDishValidation';
import { getManageEventSteps } from './manageEventForm';
import { EVENT_DESCRIPTION_MAX, EVENT_SHORT_DESCRIPTION_MAX } from './eventFormConstants';
import { isValidAttendeeEligibility } from '../../../shared/registrationSettings';
import { validateCoverImageFileSize } from './coverImageValidation';
import { isBlobPreviewUrl } from './coverImageUtils';
import { isOnlineEventFormat, normalizeEventFormat } from './eventFormatConstants';
import { isEventDatePast } from './eventTimeUtils';

export const getCoverImageSubmitBlockMessage = (
  form,
  {
    coverImageOptimizing = false,
    coverImagePendingUpload = false,
    coverImageOptimizationError = '',
  } = {},
) => {
  if (coverImageOptimizing) {
    return 'Please wait for cover image optimization to finish.';
  }

  if (coverImagePendingUpload || coverImageOptimizationError) {
    return 'Cover image optimization must complete before publishing.';
  }

  if (isBlobPreviewUrl(form.coverImagePreview) && !(form.coverImageFile instanceof File)) {
    return 'Cover image optimization must complete before publishing.';
  }

  return null;
};

const timeToMinutes = (time) => {
  const formatted = formatTimeForApi(time);
  if (!formatted) return null;
  const [hours, minutes] = formatted.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const isPositiveInt = (value) => {
  const n = Number(String(value).trim());
  return Number.isInteger(n) && n >= 1;
};

const isValidCoordinate = (value, min, max) => {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  const n = Number(raw);
  return Number.isFinite(n) && n >= min && n <= max;
};

const isValidHttpUrl = (value) => {
  try {
    const url = new URL(String(value).trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const buildResult = (errors) => ({
  valid: Object.keys(errors).length === 0,
  errors,
  messages: Object.values(errors),
});

export const validateBasicInfoStep = (form) => {
  const errors = {};

  if (!form.title?.trim()) {
    errors.title = 'Event title is required';
  }

  if (!form.type?.trim()) {
    errors.type = 'Event type is required';
  } else if (!isValidEventTypeKey(form.type)) {
    errors.type = 'Select a valid event type';
  }

  if (!String(form.totalSeats ?? '').trim()) {
    errors.totalSeats = 'Total seats is required';
  } else if (!isPositiveInt(form.totalSeats)) {
    errors.totalSeats = 'Enter a whole number of at least 1';
  }

  // if (!form.bookingDeadline) {
  //   errors.bookingDeadline = 'Booking deadline is required';
  // }

  if (!form.coverImageFile && !form.coverImagePreview) {
    errors.coverImage = 'Cover image is required';
  } else if (form.coverImageFile instanceof File) {
    const sizeResult = validateCoverImageFileSize(form.coverImageFile);
    if (!sizeResult.valid) {
      errors.coverImage = sizeResult.message;
    }
  }

  if (!form.description?.trim()) {
    errors.description = 'Description is required';
  } else if (form.description.trim().length > EVENT_DESCRIPTION_MAX) {
    errors.description = `Description must be ${EVENT_DESCRIPTION_MAX} characters or fewer`;
  }

  if (!form.wingmanDescription?.trim()) {
    errors.wingmanDescription = 'Short description is required';
  } else if (form.wingmanDescription.trim().length > EVENT_SHORT_DESCRIPTION_MAX) {
    errors.wingmanDescription = `Short description must be ${EVENT_SHORT_DESCRIPTION_MAX} characters or fewer`;
  }

  return buildResult(errors);
};

export const validateDateLocationStep = (form) => {
  const errors = {};

  if (!form.eventDate) {
    errors.eventDate = 'Event date is required';
  } else if (isEventDatePast(form.eventDate)) {
    errors.eventDate = 'Event date must be today or a future date';
  }

  if (!form.startTime) {
    errors.startTime = 'Start time is required';
  }

  if (!form.endTime) {
    errors.endTime = 'End time is required';
  }

  if (form.startTime && form.endTime) {
    const startMins = timeToMinutes(form.startTime);
    const endMins = timeToMinutes(form.endTime);
    if (startMins != null && endMins != null && endMins <= startMins) {
      errors.endTime = 'End time must be after start time';
    }
  }

  // if (form.bookingDeadline && form.eventDate) {
  //   const deadline = startOfDay(form.bookingDeadline);
  //   const eventDay = startOfDay(form.eventDate);
  //   if (deadline > eventDay) {
  //     errors.bookingDeadline = 'Booking deadline must be on or before the event date';
  //   }
  // }

  const eventFormat = normalizeEventFormat(form.eventFormat);
  const isOnline = isOnlineEventFormat(eventFormat);

  if (isOnline) {
    if (!form.onlineMeetingLink?.trim()) {
      errors.onlineMeetingLink = 'Online meeting link is required';
    } else if (!isValidHttpUrl(form.onlineMeetingLink)) {
      errors.onlineMeetingLink = 'Enter a valid URL starting with http:// or https://';
    }
  } else {
    if (!form.venueName?.trim()) {
      errors.venueName = 'Venue name is required';
    }

    if (!form.location?.trim()) {
      errors.location = 'Location is required';
    } else if (
      !isValidCoordinate(form.latitude, -90, 90) ||
      !isValidCoordinate(form.longitude, -180, 180)
    ) {
      errors.location = 'Select a location from the address suggestions';
    }
  }

  return buildResult(errors);
};

export const validateMenuStep = (form) => {
  if (form.menuSkipped) {
    return buildResult({});
  }

  const errors = {};

  form.menuSections?.forEach((section, sectionIndex) => {
    section.dishes?.forEach((dish, dishIndex) => {
      const name = dish.dishName?.trim();
      const hasDescription = Boolean(dish.description?.trim());
      const hasImage = Boolean(dish.imageFile) || Boolean(dish.imagePreview);
      const isActive = Boolean(name) || hasImage || hasDescription;
      const courseLabel = section.course ?? `Course ${sectionIndex + 1}`;

      if (!isActive) return;

      if (!name) {
        errors[dishNameFieldKey(sectionIndex, dishIndex)] = dishNameRequiredMessage(
          courseLabel,
          dishIndex,
        );
      }
    });
  });

  return buildResult(errors);
};

export const validateSettingsPoliciesStep = (form) => {
  const errors = {};

  if (!isValidAttendeeEligibility(form.attendeeEligibility)) {
    errors.attendeeEligibility = 'Select who can register for this event';
  }

  return buildResult(errors);
};

const getStepId = (stepIndex, includeSettingsStep) =>
  getManageEventSteps(includeSettingsStep)[stepIndex]?.id;

export const validateManageEventStep = (stepIndex, form, { includeSettingsStep = false } = {}) => {
  const stepId = getStepId(stepIndex, includeSettingsStep);

  switch (stepId) {
    case 'basic':
      return validateBasicInfoStep(form);
    case 'date':
      return validateDateLocationStep(form);
    case 'menu':
      return validateMenuStep(form);
    case 'settings':
      return validateSettingsPoliciesStep(form);
    case 'confirmation':
      return validateAllStepsBeforeConfirmation(form, { includeSettingsStep });
    default:
      return buildResult({});
  }
};

export const validateAllStepsBeforeConfirmation = (form, { includeSettingsStep = false } = {}) => {
  const validators = [validateBasicInfoStep, validateDateLocationStep, validateMenuStep];

  if (includeSettingsStep) {
    validators.push(validateSettingsPoliciesStep);
  }

  for (let index = 0; index < validators.length; index += 1) {
    const result = validators[index](form);
    if (!result.valid) {
      return { ...result, failedStep: index };
    }
  }

  return buildResult({});
};
